import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as api from './api.ts';
import { Map } from './map.tsx';
import {
  defaultCenter,
  type MapCircle,
  type MapPolygon,
  type MapPoint,
  type MapProjectorRef,
  type MapRegion,
} from './map-style.ts';

/**
 * The map is the platform (`docs/design-dashboard.md` §8).
 *
 * ONE live map, rendered ONCE here and mounted for the whole life of the signed
 * -in shell, sitting as a fixed layer *under* every screen. It is not remounted
 * per route — remounting would reset the operator's pan and zoom on every
 * navigation and thrash the renderer, which is the single most important rule of
 * this layer. Every screen floats over it in glass; Home wires it up to be
 * interactive (pins tappable, click-to-place), every other screen leaves it
 * ambient behind the panels — but it is always the same one instance.
 *
 * The engineering that makes "survives navigation" true is here rather than in
 * any screen:
 *
 *   1. The `<Map>` element lives in `<MapSubstrate>`, which is rendered by the
 *      Shell around `children`. The Shell stays mounted while the screen area
 *      swaps, so the map element keeps its identity and its engine instance.
 *   2. `points` is memoised on `(sites, alerting)` — **every** building, always,
 *      regardless of which one is in focus. Focus centres the camera (the `focus`
 *      prop), it never removes a building from the map: an incident anywhere must
 *      still turn its pin red even while the operator is looking at one building
 *      (the safety rule). On a plain navigation neither dep changes, so the
 *      reference is stable, the engines' marker effect (keyed on `points`) does
 *      not run, and the view is left exactly where the operator put it. The
 *      engine is created once per theme (`[dark]`), never on `points`.
 *   3. Screen-specific interaction (Home's pin-tap and place-click) is handed in
 *      through context and stored by the engines in refs, so registering or
 *      clearing it updates the live map without recreating it.
 */

export interface MapInteraction {
  /** A pin was tapped — the site id. Home opens the building card on it. */
  onSelect?: (pointId: string) => void;
  /** A draggable pin was dropped somewhere new (both engines — see map.tsx). */
  onDragEnd?: (pointId: string, latitude: number, longitude: number) => void;
  /** A draggable pin is moving — continuous (both engines). */
  onDrag?: (pointId: string, latitude: number, longitude: number) => void;
  /** The map was clicked at a coordinate. Home routes it to the place-a-building flow. */
  onMapClick?: (latitude: number, longitude: number) => void;
}

/**
 * Extra content a screen draws ON the persistent map — the building wizard's
 * draft pin, its assembly points and their radius circles. Merged over the
 * always-on estate pins, never replacing them (the safety rule: an incident
 * anywhere still turns its building red while somebody is mid-wizard). `focus`
 * — when present — steers the camera; `undefined` leaves the substrate's own
 * estate/building focus in charge, `null` forces the estate frame.
 */
export interface MapContent {
  points?: MapPoint[];
  circles?: MapCircle[];
  /** Drawn areas (0123), merged over the estate like circles. */
  polygons?: MapPolygon[];
  /**
   * `nonce`: a screen that wants the camera brought BACK to the same focus
   * after the operator has panned away (the raise panel) bumps it — the
   * engines depend on the focus values, so an identical focus would otherwise
   * never re-animate.
   */
  focus?: { latitude: number; longitude: number; span?: number; nonce?: number } | null;
  /**
   * Pixels of the map's left edge the screen's own panel covers (the wizard's
   * rail panel). The camera centres in the remaining visible area — otherwise
   * a focused pin lands exactly under the panel edge.
   */
  insetLeftPx?: number;
  /** Satellite imagery instead of the street map while this content is up. */
  satellite?: boolean;
  /**
   * Estate buildings this content redraws itself (the wizard's draggable pin
   * for the building being placed) or that sit on top of its draft — hidden so
   * there are never two pins for one building (Trav, 2026-09-30: "feels like
   * adding two buildings"). A building with something RUNNING is never hidden:
   * its red pin is the safety rule this layer exists to keep.
   */
  hideSiteIds?: string[];
  /**
   * A tap on one of THIS content's own points (the warning layer's pins) — so
   * a screen's extra pins answer to the screen that drew them, never to the
   * building card. Points it does not own still go to the interaction's
   * `onSelect`.
   */
  onSelectPoint?: (pointId: string) => void;
}

interface MapSubstrateValue {
  sites: api.Site[];
  /** `'estate'` for the roll-up, or a site id for one building. */
  view: string;
  setView: (v: string) => void;
  /**
   * True once the org has more than one building — the estate layer exists, so
   * a single-building focus is escapable and Home offers the "All buildings"
   * return control.
   */
  estateCapable: boolean;
  /** Site names with something running — the pins the map draws red. */
  alerting: Set<string>;
  /** The active screen registers its map interaction here; `{}` clears it. */
  setInteraction: (i: MapInteraction) => void;
  /** The active screen's extra map content ({@link MapContent}); `null` clears it. */
  setContent: (c: MapContent | null) => void;
  /**
   * The ONE source of truth for "an inspect overlay / modal is open" (owner
   * correction 2026-09-07). While any is open the persistent map must not pan or
   * take focus behind it — the overlay and its frost backdrop own input. Every
   * {@link InspectOverlay} registers itself through {@link openOverlay} /
   * {@link closeOverlay} on mount/unmount, so this stays true no matter which
   * screen opened it.
   */
  overlayOpen: boolean;
  /** Register an overlay as open (ref-counted, so nested/stacked overlays are safe). */
  openOverlay: () => void;
  /** Release one overlay's registration. */
  closeOverlay: () => void;
  /**
   * Where a coordinate is on screen right now (viewport pixels), or `null`
   * before the engine is up or on the unavailable panel. Stable; read it per
   * frame — Home's building callout follows its pin through pan and zoom with it.
   */
  project: (latitude: number, longitude: number) => { x: number; y: number } | null;
}

const Ctx = createContext<MapSubstrateValue | null>(null);

/**
 * The persistent map's visible box, updated when the camera settles. Its own
 * context so a pan re-renders only the layers that read it, never every
 * consumer of the substrate.
 */
const RegionCtx = createContext<MapRegion | null>(null);

/** Where the map is looking right now (settled), or `null` before it has said. */
export function useMapRegion(): MapRegion | null {
  return useContext(RegionCtx);
}

/** Read the persistent map's shared state — the estate/building view and the
 *  interaction hook. Home uses it; other screens do not need it. */
export function useMapSubstrate(): MapSubstrateValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useMapSubstrate must be used inside <MapSubstrate>');
  return v;
}

/**
 * The persistent map as a screen's canvas: register content to draw and taps to
 * receive, cleared automatically on unmount. The building wizard uses it so the
 * ONE substrate map — not a second embedded engine — is the wizard's map.
 *
 * Null-safe like {@link useRegisterOverlay}: outside a `<MapSubstrate>` (a unit
 * test mounting the screen alone) both setters are no-ops.
 */
export function useMapCanvas(): {
  setContent: (c: MapContent | null) => void;
  setInteraction: (i: MapInteraction) => void;
} {
  const v = useContext(Ctx);
  const setContent = v?.setContent;
  const setInteraction = v?.setInteraction;
  useEffect(() => {
    if (!setContent || !setInteraction) return;
    return () => {
      setContent(null);
      setInteraction({});
    };
  }, [setContent, setInteraction]);
  return {
    setContent: setContent ?? (() => undefined),
    setInteraction: setInteraction ?? (() => undefined),
  };
}

/**
 * Register a mounted overlay/modal as open against the ONE source of truth, and
 * release it on unmount — the mechanism {@link InspectOverlay} uses so opening
 * any overlay tells the map to stop panning (A, owner correction 2026-09-07).
 *
 * Deliberately null-safe: it reads the context directly rather than through
 * {@link useMapSubstrate}, so an overlay rendered outside a `<MapSubstrate>` (a
 * unit test mounting a screen on its own) is a no-op instead of a throw.
 */
export function useRegisterOverlay(): void {
  const v = useContext(Ctx);
  // Read the two stable callbacks off the context rather than depending on the
  // whole context value: `v` is a new object every 30s (the events poll bumps
  // `alerting`), and depending on `[v]` re-ran this effect — a closeOverlay +
  // openOverlay churn — on every poll. `openOverlay`/`closeOverlay` are
  // `useCallback`-stable, so depending on them runs the effect exactly once per
  // mount. Null-safe: an overlay mounted outside a `<MapSubstrate>` (a unit test
  // mounting a screen on its own) is a no-op instead of a throw.
  const openOverlay = v?.openOverlay;
  const closeOverlay = v?.closeOverlay;
  useEffect(() => {
    if (!openOverlay || !closeOverlay) return;
    openOverlay();
    return closeOverlay;
  }, [openOverlay, closeOverlay]);
}

/* --------------------------------------------------------- view persistence */

const VIEW_PREFIX = 'evac.home-view:';

function readView(orgId: string): string | null {
  try {
    return localStorage.getItem(`${VIEW_PREFIX}${orgId}`);
  } catch {
    return null;
  }
}

function writeView(orgId: string, view: string): void {
  try {
    localStorage.setItem(`${VIEW_PREFIX}${orgId}`, view);
  } catch {
    // A lost preference reappears on the default next load — a mild repeat, not
    // a trap.
  }
}

/**
 * The view to open on. A single-building org is pinned to its building; a
 * multi-building org honours the remembered choice (validated against the sites
 * that still exist), then defaults to the estate.
 */
function initialView(orgId: string, sites: api.Site[], estateCapable: boolean): string {
  if (!estateCapable) return sites[0]?.id ?? 'estate';
  const stored = readView(orgId);
  if (stored === 'estate') return 'estate';
  if (stored && sites.some((s) => s.id === stored)) return stored;
  return 'estate';
}

/** Map points for a set of buildings — placed only, red where something runs. */
function sitePoints(sites: api.Site[], alerting: Set<string>): MapPoint[] {
  return sites
    .filter((s) => s.latitude != null && s.longitude != null)
    .map((s) => ({
      id: s.id,
      latitude: s.latitude!,
      longitude: s.longitude!,
      label: s.name,
      kind: alerting.has(s.name) ? ('alerting' as const) : ('site' as const),
    }));
}

/**
 * The persistent map layer and the context around it.
 *
 * Rendered by the Shell around every screen. It draws the org's building pins as
 * its default content (the estate), goes red on a building with something
 * running, and never remounts on navigation.
 */
export function MapSubstrate({
  orgId,
  sites,
  onFocusSite,
  children,
}: {
  orgId: string;
  sites: api.Site[];
  /** Push a real building selection up to the app so a raise opens on it (H2). */
  onFocusSite?: (siteId: string) => void;
  children: ReactNode;
}) {
  const estateCapable = sites.length > 1;

  const [view, setViewState] = useState<string>(() => initialView(orgId, sites, estateCapable));
  // Re-init when the org changes, and once more the first time sites arrive.
  //
  // On a cold load `sites` is `[]` while the query is in flight, so a stored
  // *building* view fails `initialView`'s validation (the building is not yet in
  // the list) and falls back to `'estate'`. Gating the effect on a
  // sites-present sentinel — false→true exactly once — re-runs the restore the
  // moment the buildings land, so the stored building wins instead of estate.
  // It is a boolean, not `sites` itself, so an ordinary refetch (true→true)
  // does NOT re-run and never yanks an admin out of the building they drilled
  // into. (There is no exhaustive-deps lint rule configured.)
  const hasSites = sites.length > 0;
  useEffect(() => {
    const restored = initialView(orgId, sites, estateCapable);
    setViewState(restored);
    // Keep the app-wide site selection in step with what the map opens on, so a
    // raise from the chrome opens on the building the operator is looking at
    // rather than site[0] (H2). `'estate'` carries no single building, so it is
    // left alone.
    if (restored !== 'estate' && sites.some((s) => s.id === restored)) onFocusSite?.(restored);
    // Deps are deliberately just `orgId` and the sites-present sentinel (there is
    // no exhaustive-deps lint rule configured).
  }, [orgId, hasSites]);

  const setView = useCallback(
    (v: string) => {
      setViewState(v);
      writeView(orgId, v);
      // A real building selection carries to every other screen, so a raise
      // opens on the building the operator is looking at rather than site[0].
      if (v !== 'estate') onFocusSite?.(v);
    },
    [orgId, onFocusSite],
  );

  // Which buildings have something running — read once here so the map draws the
  // alerting pins even on the screens where Home is not mounted to draw a strip.
  const events = useQuery({
    queryKey: ['events', orgId],
    queryFn: () => api.listEvents(orgId),
    enabled: Boolean(orgId),
    // The map's alerting pins are always-on status on every screen, so they must
    // not go stale: poll so a just-raised incident turns its pin red without a
    // manual reload. 30s aligns with the escalations query on Home's strip.
    refetchInterval: 30_000,
  });
  // A building's pin turns red only when an alert has actually gone out
  // (`active`). A `pending_second_confirm` event has sent nothing — painting
  // its building as alerting would assert an emergency that has not launched
  // (the state-assertion bug class). Pending is surfaced on Home's strip as
  // "awaiting authorisation", not as a red pin.
  const alerting = useMemo(
    () =>
      new Set(
        (events.data?.events ?? []).filter((e) => e.state === 'active').map((e) => e.site_name),
      ),
    [events.data],
  );

  const onEstate = estateCapable && view === 'estate';
  const focusSite = onEstate ? null : (sites.find((s) => s.id === view) ?? sites[0] ?? null);

  // EVERY building is drawn, every time — never just the one in focus. Removing
  // the others was a one-way door: an incident at another building never turned
  // its pin red while the operator was focused on one, so a new emergency
  // elsewhere was invisible. Focus is a *camera* concern (the `focus` prop
  // below), not a filter on which buildings exist on the map.
  // Extra content the active screen draws on the substrate (the wizard's draft
  // pin and circles). Held here so the ONE map instance draws it — never a
  // second engine mounted beside the first.
  const [content, setContent] = useState<MapContent | null>(null);
  const setContentStable = useCallback((c: MapContent | null) => setContent(c), []);
  const estatePoints = useMemo(() => sitePoints(sites, alerting), [sites, alerting]);
  // The screen's extra content rides ON TOP of the estate — merged, never
  // replacing, so every building pin (and its alerting red) stays on the map
  // while the wizard draws its draft over it.
  const points = useMemo(() => {
    // Only once we KNOW what is running (security review L3): before the events
    // query has answered — or when it failed — every building could be the
    // one with an incident, so nothing is hidden.
    const hide = new Set(events.isSuccess ? (content?.hideSiteIds ?? []) : []);
    const estate =
      hide.size > 0
        ? estatePoints.filter((p) => p.kind === 'alerting' || !hide.has(p.id))
        : estatePoints;
    return content?.points?.length ? [...estate, ...content.points] : estate;
  }, [estatePoints, content, events.isSuccess]);
  // Where to drop the base map when there is nothing placed to frame (a canvas
  // for an org with no coordinates yet).
  const center = points[0] ?? defaultCenter();
  // Centre/zoom the camera on the building in focus without hiding the rest.
  // `null` on the estate roll-up (fit them all) or when the focused building has
  // no coordinates yet (nothing to centre on). A screen's registered content
  // may override it (the wizard centring on the pin just placed); `undefined`
  // there leaves this substrate focus in charge.
  const focus =
    content && content.focus !== undefined
      ? content.focus
      : focusSite && focusSite.latitude != null && focusSite.longitude != null
        ? { latitude: focusSite.latitude, longitude: focusSite.longitude }
        : null;

  const [region, setRegion] = useState<MapRegion | null>(null);

  const [interaction, setInteraction] = useState<MapInteraction>({});
  const setInteractionStable = useCallback((i: MapInteraction) => setInteraction(i), []);

  // The ONE "an overlay is open" source of truth. Ref-counted so two overlays
  // (a detail plus a CTA form, say) never leave it stuck open when one closes.
  const [overlayCount, setOverlayCount] = useState(0);
  const openOverlay = useCallback(() => setOverlayCount((c) => c + 1), []);
  const closeOverlay = useCallback(() => setOverlayCount((c) => Math.max(0, c - 1)), []);
  const overlayOpen = overlayCount > 0;

  // The mounted engine fills this; `project` reads it, so the function handed
  // out through context never changes identity when the engine swaps.
  const projectorRef = useRef<MapProjectorRef['current']>(null);
  const project = useCallback(
    (latitude: number, longitude: number) => projectorRef.current?.(latitude, longitude) ?? null,
    [],
  );

  // A new object each render would force every context consumer to re-render on
  // any Shell render, so it is memoised on the values it actually carries.
  const value = useMemo<MapSubstrateValue>(
    () => ({
      sites,
      view,
      setView,
      estateCapable,
      alerting,
      setInteraction: setInteractionStable,
      setContent: setContentStable,
      overlayOpen,
      openOverlay,
      closeOverlay,
      project,
    }),
    [
      sites,
      view,
      setView,
      estateCapable,
      alerting,
      setInteractionStable,
      setContentStable,
      overlayOpen,
      openOverlay,
      closeOverlay,
      project,
    ],
  );

  // The map is interactive only where a screen has registered interaction —
  // i.e. Home. Everywhere else it is ambient cartography behind glass, and a
  // keyboard or screen-reader user must not be able to tab into its zoom/pan
  // controls sitting behind the content. `inert` takes the whole substrate out
  // of the tab order and makes it gesture-inert; `aria-hidden` hides it from the
  // accessibility tree. Only interactive Home exposes the map to AT.
  //
  // An open inspect overlay/modal takes the map out of play even on Home: the
  // overlay and its frost backdrop own input, so the map must neither pan nor be
  // reachable behind the modal (A, owner correction 2026-09-07). Gating
  // `mapInteractive` here keeps the `inert`/`aria-hidden` treatment consistent
  // with the pointer-events story — one flag, one truth.
  const mapInteractive = Boolean(interaction.onSelect || interaction.onMapClick) && !overlayOpen;

  return (
    <Ctx.Provider value={value}>
      <RegionCtx.Provider value={region}>
        {/*
        The one live map. Fixed and full-viewport at z-0, so the rail and every
        screen's glass float over live cartography rather than a flat colour. It
        is drawn even before a single building has coordinates — an empty grey
        box is an outage, not an empty state — via the fallback centre.
      */}
        <div className="fixed inset-0 z-0" inert={!mapInteractive} aria-hidden={!mapInteractive}>
          <Map
            points={points}
            circles={content?.circles}
            polygons={content?.polygons}
            height="100%"
            fallbackCenter={center}
            focus={focus}
            insetLeft={content?.insetLeftPx}
            projectorRef={projectorRef}
            satellite={content?.satellite}
            onSelect={
              interaction.onSelect || content?.onSelectPoint
                ? (id) => {
                    if (content?.onSelectPoint && content.points?.some((p) => p.id === id))
                      content.onSelectPoint(id);
                    else interaction.onSelect?.(id);
                  }
                : undefined
            }
            onRegionChange={setRegion}
            onDragEnd={interaction.onDragEnd}
            onDrag={interaction.onDrag}
            onClick={interaction.onMapClick}
          />
        </div>
        {children}
      </RegionCtx.Provider>
    </Ctx.Provider>
  );
}
